import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

const navLinks = [
  { label: 'Home', path: '/' },
  {
    label: 'Collections',
    path: '/collections',
    dropdown: [
      { label: 'Living Room', path: '/collections/living-room', icon: 'ri-sofa-line' },
      { label: 'Bedroom', path: '/collections/bedroom', icon: 'ri-hotel-bed-line' },
      { label: 'Dining Room', path: '/collections/dining-room', icon: 'ri-restaurant-line' },
      { label: 'Home Office', path: '/collections/home-office', icon: 'ri-computer-line' },
      { label: 'Outdoor', path: '/collections/outdoor', icon: 'ri-plant-line' },
    ],
  },
  { label: 'About', path: '/about' },
  { label: 'Gallery', path: '/gallery' },
  { label: 'Showroom', path: '/showroom' },
  { label: 'FAQ', path: '/faq' },
];

const LOGO_URL = 'https://static.readdy.ai/image/81164d3ba0f00c2a7e247a9f180c9b75/f0fddf85d747fa5ad2e4c99896af0d57.png';

export default function Navbar() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setMenuOpen(false);
    setDropdownOpen(false);
  }, [location]);

  return (
    <>
      <header className="fixed top-0 left-0 right-0 z-50 bg-dark border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 flex items-center justify-between h-20">

          {/* Logo — always white on black header */}
          <Link to="/" className="cursor-pointer flex-shrink-0">
            <img
              src={LOGO_URL}
              alt="Eclectic Home Furniture"
              style={{
                height: '40px',
                width: 'auto',
                display: 'block',
                filter: 'brightness(0) invert(1)',
              }}
            />
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-1">
            {navLinks.map((link) =>
              link.dropdown ? (
                <div
                  key={link.path}
                  className="relative"
                  onMouseEnter={() => setDropdownOpen(true)}
                  onMouseLeave={() => setDropdownOpen(false)}
                >
                  <span className="flex items-center gap-1 px-4 py-2 text-xs font-medium uppercase tracking-[0.12em] cursor-pointer whitespace-nowrap text-white/70 hover:text-white transition-colors select-none">
                    {link.label}
                    <i className="ri-arrow-down-s-line text-xs" />
                  </span>
                  {dropdownOpen && (
                    <div
                      className="absolute top-full left-0 w-56 bg-white border border-cream-dark rounded-xl overflow-hidden"
                      style={{ boxShadow: '0 8px 32px rgba(0,0,0,0.15)', zIndex: 100 }}
                    >
                      {link.dropdown.map((item) => (
                        <Link
                          key={item.path}
                          to={item.path}
                          className="flex items-center gap-3 px-5 py-3.5 text-sm text-dark/70 hover:text-dark hover:bg-cream-mid transition-colors border-b border-cream-dark last:border-0"
                        >
                          <div className="w-5 h-5 flex items-center justify-center">
                            <i className={`${item.icon} text-gold`} />
                          </div>
                          {item.label}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`px-4 py-2 text-xs font-medium uppercase tracking-[0.12em] cursor-pointer whitespace-nowrap transition-colors ${
                    location.pathname === link.path
                      ? 'text-gold'
                      : 'text-white/70 hover:text-white'
                  }`}
                >
                  {link.label}
                </Link>
              )
            )}
          </nav>

          {/* CTAs */}
          <div className="hidden lg:flex items-center gap-3 flex-shrink-0">
            <Link
              to="/showroom"
              className="border border-white/30 text-white px-4 py-2 rounded-lg text-xs font-semibold uppercase tracking-wider hover:border-white/60 hover:bg-white/5 transition-all cursor-pointer whitespace-nowrap"
            >
              Visit Showroom
            </Link>
            <Link
              to="/collections"
              className="bg-gold text-white px-5 py-2 rounded-lg text-xs font-bold uppercase tracking-wider hover:bg-gold-dark transition-colors cursor-pointer whitespace-nowrap"
            >
              Shop Collection
            </Link>
          </div>

          {/* Mobile Hamburger */}
          <button
            onClick={() => setMenuOpen(!menuOpen)}
            className="lg:hidden w-10 h-10 flex items-center justify-center cursor-pointer text-white"
          >
            <i className={`text-xl ${menuOpen ? 'ri-close-line' : 'ri-menu-3-line'}`} />
          </button>
        </div>

        {/* Mobile Menu */}
        {menuOpen && (
          <div className="lg:hidden bg-dark border-t border-white/10 px-6 py-6">
            {navLinks.map((link) => (
              <div key={link.path}>
                <Link
                  to={link.path}
                  className="block py-3 text-white/70 font-medium border-b border-white/10 hover:text-white transition-colors text-sm uppercase tracking-wider"
                >
                  {link.label}
                </Link>
                {link.dropdown && (
                  <div className="pl-4">
                    {link.dropdown.map((item) => (
                      <Link
                        key={item.path}
                        to={item.path}
                        className="flex items-center gap-2 py-2.5 text-white/50 text-sm hover:text-gold transition-colors"
                      >
                        <div className="w-4 h-4 flex items-center justify-center">
                          <i className={`${item.icon} text-gold text-xs`} />
                        </div>
                        {item.label}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ))}
            <div className="mt-5 flex flex-col gap-3">
              <Link
                to="/collections"
                className="text-center bg-gold text-white px-4 py-3 rounded-lg text-xs font-bold uppercase tracking-wider cursor-pointer whitespace-nowrap"
              >
                Shop Collection
              </Link>
              <Link
                to="/showroom"
                className="text-center border border-white/30 text-white px-4 py-3 rounded-lg text-xs font-semibold uppercase tracking-wider cursor-pointer whitespace-nowrap"
              >
                Visit Showroom
              </Link>
            </div>
          </div>
        )}
      </header>

      {/* Floating Mobile CTA */}
      <Link
        to="/collections"
        className="fixed bottom-6 right-6 z-50 lg:hidden bg-gold text-white px-5 py-3 rounded-full flex items-center gap-2 font-bold text-xs uppercase tracking-wider cursor-pointer whitespace-nowrap"
        style={{ boxShadow: '0 4px 20px rgba(232,25,44,0.4)' }}
      >
        <div className="w-4 h-4 flex items-center justify-center">
          <i className="ri-store-2-line" />
        </div>
        Shop Now
      </Link>
    </>
  );
}
